import { Texto, Vertical } from '@gravitate-js/excalibrr'
import { theme } from 'antd'
import { useEffect, useRef } from 'react'
import { useGraviChatStore } from '../store'
import type { GraviToolCall } from '../types'
import { EmptyState } from './EmptyState'
import { MessageBubble } from './MessageBubble'
import { ResumedNotice } from './ResumedNotice'
import { ToolCallCard } from './ToolCallCard'
import { WorkingIndicator } from './WorkingIndicator'

type MessageOut = {
  id: string
  role: 'user' | 'assistant'
  text: string
  task_id?: string | null
  finalized: boolean
  created_at: string
  tool_calls: Array<{ name?: string; input?: Record<string, unknown> }>
  output_files: Array<{
    filename: string
    mime_type: string
    size_bytes: number
  }>
  attachments: Array<{
    filename: string
    mime_type: string
    size_bytes: number
    content_b64?: string | null
    url?: string | null
  }>
}

// Derived state of the in-flight turn, computed by the drawer from the live stream
// status + a poll of gravitutor's task status. `null` = no turn pending.
export type TurnState = 'working' | 'stalled' | 'error' | null

// Stable empty references used when the live stream doesn't belong to this thread,
// so a non-owning thread doesn't churn renders on every store update.
const EMPTY_TOOL_CALLS: GraviToolCall[] = []
const EMPTY_RESUMED: number[] = []

export type MessageListProps = {
  messages: MessageOut[]
  turnState?: TurnState
  turnErrorText?: string
  // The store holds a single in-flight stream. When the displayed thread does NOT
  // own that stream (the user switched chats mid-turn), this is false and the live
  // stream state must be ignored so the spinner/timer/tool-calls/streamed text stay
  // with the chat they belong to.
  streamActive?: boolean
}

export function MessageList({
  messages,
  turnState = null,
  turnErrorText,
  streamActive = true,
}: MessageListProps) {
  const { token } = theme.useToken()
  const streamingTextRaw = useGraviChatStore((s) => s.streamingText)
  const toolCallsRaw = useGraviChatStore((s) => s.toolCalls)
  const resumedAtRaw = useGraviChatStore((s) => s.resumedAt)
  const streamingText = streamActive ? streamingTextRaw : ''
  const toolCalls = streamActive ? toolCallsRaw : EMPTY_TOOL_CALLS
  const resumedAt = streamActive ? resumedAtRaw : EMPTY_RESUMED

  const isEmpty = messages.length === 0 && turnState === null && !streamingText

  // Keep the newest content in view. scrollIntoView walks up to the real scroll
  // container (the AntD drawer body), so it works regardless of the flex layout.
  const bottomRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    try {
      bottomRef.current?.scrollIntoView({ block: 'end' })
    } catch {
      // scrollIntoView is a no-op/unsupported in jsdom — ignore
    }
  }, [messages.length, streamingText, toolCalls.length, turnState])

  if (isEmpty) {
    return (
      <div
        style={{
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EmptyState />
      </div>
    )
  }

  // A user message and its assistant reply share a task_id; the wall-clock gap
  // between the two created_at timestamps is the time that turn took to produce.
  const userSentAtByTask = new Map<string, number>()
  for (const m of messages) {
    if (m.role === 'user' && m.task_id)
      userSentAtByTask.set(m.task_id, new Date(m.created_at).getTime())
  }

  return (
    <div style={{ padding: 12 }}>
      <Vertical gap={8}>
        {messages.map((m) => {
          const sentAt =
            m.role === 'assistant' && m.task_id
              ? userSentAtByTask.get(m.task_id)
              : undefined
          const durationMs =
            sentAt !== undefined
              ? Math.max(0, new Date(m.created_at).getTime() - sentAt)
              : undefined
          return (
            <MessageBubble
              key={m.id}
              messageRole={m.role}
              text={m.text}
              taskId={m.task_id ?? undefined}
              outputFiles={m.output_files}
              attachments={m.attachments}
              durationMs={durationMs}
            />
          )
        })}
        {resumedAt.map((cursor) => (
          <ResumedNotice key={`resume-${cursor}`} />
        ))}
        {toolCalls.map((tc) => (
          <ToolCallCard key={`tc-${tc.cursor}`} toolCall={tc} />
        ))}
        {turnState === 'working' && (
          <WorkingIndicator streamingText={streamingText} />
        )}
        {(turnState === 'error' || turnState === 'stalled') && (
          <div
            style={{
              alignSelf: 'flex-start',
              maxWidth: '85%',
              background: token.colorErrorBg,
              padding: '8px 12px',
              borderRadius: 8,
            }}
          >
            <Texto style={{ color: token.colorError }}>
              {turnState === 'error'
                ? (turnErrorText ?? 'Gravi ran into a problem')
                : "Gravi hasn't responded — it may be stuck."}
            </Texto>
          </div>
        )}
        <div ref={bottomRef} />
      </Vertical>
    </div>
  )
}
